import type { ChordBorrowing, ChordFunction } from './types.js'

interface DescribeInput {
  chordFunction: ChordFunction
  borrowing: ChordBorrowing
}

/**
 * Look up a one-line rationale (≤60 chars) for a (chordFunction, borrowing)
 * combination. These templates are owned by Bowie and reviewed by Joni.
 */
const TEMPLATES: Record<string, string> = {
  // Major-scale diatonic
  'I|null': 'Tonic.',
  'ii|null': 'Supertonic — predominant.',
  'iii|null': 'Mediant — gentle, often passing.',
  'IV|null': 'Subdominant — predominant motion.',
  'V|null': 'Dominant — leading-tone resolves to tonic.',
  'vi|null': 'Submediant — relative-minor home.',
  'vii°|null': 'Leading-tone — strong pull to tonic.',

  // Minor-scale diatonic
  'i|null': 'Tonic.',
  'ii°|null': 'Supertonic diminished — predominant.',
  '♭III|null': 'Mediant — relative major.',
  'iv|null': 'Subdominant — predominant motion.',
  'v|null': 'Dominant (natural minor).',
  '♭VI|null': 'Submediant.',
  '♭VII|null': 'Subtonic — natural minor.',

  // Minor-flavored mode diatonic chords (Dorian / Phrygian / Mixolydian / Locrian).
  // Bowie-owned register: `<Role> — <context>.`
  'i°|null': 'Tonic diminished — Locrian center.',
  'iii°|null': 'Mediant diminished — Mixolydian color.',
  'v°|null': 'Dominant diminished — Phrygian color.',
  'vi°|null': 'Submediant diminished — Dorian color.',
  '♭iii|null': 'Mediant — Locrian.',
  '♭V|null': 'Major triad on flat-five.',
  '♭vii|null': 'Subtonic minor — Phrygian color.',

  // Lydian-distinctive diatonic chords (v1.5b).
  'II|null': 'Supertonic major — Lydian color.',
  '#iv°|null': 'Raised-fourth diminished — Lydian characteristic.',
  'vii|null': 'Leading-tone minor — Lydian.',

  // Harmonic-minor-distinctive diatonic chord (v1.5c).
  '♭III+|null': 'Augmented mediant — harmonic minor.',

  // Parallel-mode borrowings (chordFunction may also appear in minor-diatonic
  // above; the borrowing field disambiguates).
  '♭III|parallel-mode': 'Borrowed from parallel minor.',
  'iv|parallel-mode': 'Minor subdominant — borrowed from parallel minor.',
  '♭VI|parallel-mode': 'Borrowed from parallel minor.',
  '♭VII|parallel-mode': 'Borrowed from parallel minor — Mixolydian color.',
  '♭II|parallel-mode': 'Neapolitan — Phrygian color.',
  // (When borrowing into minor from parallel major)
  'IV|parallel-mode': 'Major subdominant — borrowed from parallel major.',

  // Secondary dominants
  'V/ii|secondary-dominant': 'Secondary dominant of ii.',
  'V/IV|secondary-dominant': 'Secondary dominant of IV.',
  'V/V|secondary-dominant': 'Secondary dominant of V.',
  'V/vi|secondary-dominant': 'Secondary dominant of vi.',

  // Chromatic outliers
  'chromatic|null': 'Outside the key.',
}

export const describeChord = ({ chordFunction, borrowing }: DescribeInput): string => {
  const key = `${chordFunction}|${borrowing ?? 'null'}`
  return TEMPLATES[key] ?? 'Outside the key.'
}
